import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Button, Form, Table, Alert, Card, Badge, Pagination } from 'react-bootstrap';
import addIcon from '../assets/icons/add.svg';
import closeIcon from '../assets/icons/close.svg';
import searchIcon from '../assets/icons/search.svg';
import documentIcon from '../assets/icons/document.svg';
import { SearchableCustomerSelect } from './SearchableCustomerSelect';
import { SearchableVehicleSelect } from './SearchableVehicleSelect';
import { renderPaymentMethodOptions } from '../utils/paymentMethodOptions';
import { CustomerLink, VehicleLink } from './EntityLinks';
import { TransactionForm } from './TransactionForm';
import { getAgentId } from '../utils/agentId';
import { buildTransactionPayload, pricingTiers } from '../utils/transactionPayload';
import { calculateTaxFrontend } from '../config/taxRates';
import { useURLParams } from '../hooks/useURLParams';
import { useColumnConfig } from '../hooks/useColumnConfig';
import { ColumnConfigDropdown } from './shared/ColumnConfigDropdown';

const COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'customer', label: 'Customer' },
  { key: 'type', label: 'Type' },
  { key: 'vehicle', label: 'Vehicle' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'end_date', label: 'End Date' },
  { key: 'amount', label: 'Amount' },
  { key: 'actions', label: 'Actions' },
];

/**
 * TransactionList component displays all transactions in a table
 * and provides navigation to detail view and creation
 */
export function TransactionList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { transactions, setTransactions, apiCall, user } = useOutletContext();
  const [showNewTransactionForm, setShowNewTransactionForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // URL-synced filters and pagination
  const { getParam, setParam, setParams } = useURLParams({
    f_type: '', f_start_date: '', f_end_date: '', f_store_id: '', f_vehicle_id: '', page: 0,
  });

  // Column config from URL
  const { visibleColumns, isVisible, toggleColumn, allColumns } = useColumnConfig(COLUMNS);

  // Read filters from URL
  const filters = {
    type: getParam('f_type'),
    start_date: getParam('f_start_date'),
    end_date: getParam('f_end_date'),
    store_id: getParam('f_store_id'),
    vehicle_id: getParam('f_vehicle_id'),
  };
  const urlPage = getParam('page');

  // Auto-open form if navigated from Quick Actions
  useEffect(() => {
    if (location.state?.openAddForm) {
      setShowNewTransactionForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);
  const [formData, setFormData] = useState({
    customer_id: '',
    type: 'rental',
    store_id: '',
    vehicle_id: '',
    insurance_id: '',
    start_date: new Date().toISOString().split('T')[0],
    unit: 'day',
    quantity: '',
    amount: '',
    contract_file_url: '',
    notes: '',
    isPaid: true,
    payment_method: 'cash_app',
  });
  const [formError, setFormError] = useState(null);

  // Local filter form state (synced to URL on apply)
  const [localFilters, setLocalFilters] = useState(filters);

  const [stores, setStores] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: urlPage * 50,
    total: 0,
  });
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // Load transactions, stores, and agents on component mount
  useEffect(() => {
    loadStoresAndAgents();
    loadFilteredTransactions(filters, urlPage * 50);
  }, [apiCall]);

  const loadStoresAndAgents = async () => {
    try {
      const storesRes = await apiCall(`${API_BASE_URL}/stores`);
      setStores(storesRes?.data || []);
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const handleGenerateContract = async (transactionId, vehicleId) => {
    if (!vehicleId) {
      alert('Cannot generate contract: Transaction must have a vehicle');
      return;
    }

    try {
      const response = await apiCall(
        `${API_BASE_URL}/transactions/${transactionId}/generate-contract`,
        { method: 'POST' }
      );

      // Decode base64 PDF and trigger download
      const base64PDF = response.data.pdfBase64;
      const binaryString = window.atob(base64PDF);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('Contract generated and downloaded successfully!');
    } catch (err) {
      alert('Failed to generate contract: ' + err.message);
    }
  };

  // Load transactions with filters and pagination
  const loadFilteredTransactions = async (filterParams = {}, pageOffset = 0) => {
    try {
      const queryParams = new URLSearchParams({
        limit: String(pagination.limit),
        offset: String(pageOffset),
      });

      // Add active filters
      const f = { ...filters, ...filterParams };
      if (f.type) queryParams.append('type', f.type);
      if (f.start_date) queryParams.append('start_date', f.start_date);
      if (f.end_date) queryParams.append('end_date', f.end_date);
      if (f.store_id) queryParams.append('store_ids', f.store_id);
      if (f.vehicle_id) queryParams.append('vehicle_ids', f.vehicle_id);

      const response = await apiCall(
        `${API_BASE_URL}/transactions?${queryParams.toString()}`
      );

      if (response?.data) {
        setFilteredTransactions(response.data);
        setPagination({
          limit: response.limit || 50,
          offset: response.offset || 0,
          total: response.total || 0,
        });
      }
    } catch (error) {
      console.error('Error loading filtered transactions:', error);
    }
  };

  // Handle filter changes (local state only, applied on button click)
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters - sync to URL and reload
  const applyFilters = async () => {
    setParams({
      f_type: localFilters.type,
      f_start_date: localFilters.start_date,
      f_end_date: localFilters.end_date,
      f_store_id: localFilters.store_id,
      f_vehicle_id: localFilters.vehicle_id,
      page: '',
    });
    setPagination(prev => ({ ...prev, offset: 0 }));
    await loadFilteredTransactions(localFilters, 0);
    setShowFilters(false);
  };

  // Reset filters
  const resetFilters = async () => {
    const empty = { type: '', start_date: '', end_date: '', store_id: '', vehicle_id: '' };
    setLocalFilters(empty);
    setParams({ f_type: '', f_start_date: '', f_end_date: '', f_store_id: '', f_vehicle_id: '', page: '' });
    setPagination({ limit: 50, offset: 0, total: 0 });
    setFilteredTransactions([]);
  };

  // Pagination handlers
  const goToNextPage = async () => {
    const nextOffset = pagination.offset + pagination.limit;
    if (nextOffset < pagination.total) {
      const nextPage = Math.floor(nextOffset / pagination.limit);
      setParam('page', nextPage, { replace: true });
      await loadFilteredTransactions(filters, nextOffset);
    }
  };

  const goToPreviousPage = async () => {
    const prevOffset = Math.max(0, pagination.offset - pagination.limit);
    const prevPage = Math.floor(prevOffset / pagination.limit);
    setParam('page', prevPage > 0 ? prevPage : '', { replace: true });
    await loadFilteredTransactions(filters, prevOffset);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'customer_id' || name === 'vehicle_id' || name === 'insurance_id' || name === 'daily_rate' || name === 'amount' || name === 'store_id' || name === 'quantity' ? (value === '' ? '' : parseFloat(value)) : value,
    }));
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (formData.type === 'fee' || formData.type === 'late_fee') {
      if (!formData.start_date || !formData.amount) {
        setFormError('Please fill in all required fields');
        return;
      }
      const expenseAmount = parseFloat(formData.amount);
      if (isNaN(expenseAmount) || expenseAmount <= 0) {
        setFormError('Please enter a valid amount');
        return;
      }
      if (formData.isPaid !== false && !formData.payment_method) {
        setFormError('Please select a payment method or mark as unpaid');
        return;
      }
    } else {
      if (!formData.vehicle_id || !formData.start_date || !formData.quantity) {
        setFormError('Please fill in all required fields');
        return;
      }
      if (!selectedVehicle) {
        setFormError('Please select a vehicle');
        return;
      }
      if (formData.isPaid !== false && !formData.payment_method) {
        setFormError('Please select a payment method or mark as unpaid');
        return;
      }
    }

    try {
      const agentId = await getAgentId(apiCall, user);
      if (agentId === null) {
        setFormError('Could not determine agent ID. Please refresh and try again.');
        return;
      }

      const dataToSend = buildTransactionPayload(
        { ...formData, vehicle_class: selectedVehicle?.class },
        agentId
      );

      const response = await apiCall(
        `${API_BASE_URL}/transactions`,
        {
          method: 'POST',
          body: JSON.stringify(dataToSend),
        }
      );

      if (response.success) {
        setFormData({
          customer_id: '',
          type: 'rental',
          store_id: '',
          vehicle_id: '',
          insurance_id: '',
          start_date: new Date().toISOString().split('T')[0],
          unit: 'day',
          quantity: '',
          amount: '',
          contract_file_url: '',
          notes: '',
          isPaid: true,
          payment_method: 'cash_app',
        });
        setShowNewTransactionForm(false);
        setFormError(null);
        alert('Transaction added successfully');

        // Refetch all transactions to get updated list
        await loadFilteredTransactions();
      } else {
        setFormError(response.error || 'Failed to add transaction');
      }
    } catch (error) {
      setFormError(error.message || 'Error adding transaction');
    }
  };

  // Use joined data from backend
  const getCustomerName = (transaction) => {
    if (transaction.first_name && transaction.last_name) {
      return `${transaction.first_name} ${transaction.last_name}`;
    }
    return `Customer #${transaction.customer_id}`;
  };

  const getVehicleInfo = (transaction) => {
    if (transaction.make && transaction.model) {
      return `${transaction.year || ''} ${transaction.make} ${transaction.model}`.trim();
    }
    return `Vehicle #${transaction.vehicle_id}`;
  };

  const renderCell = (key, transaction) => {
    switch (key) {
      case 'id': return transaction.id;
      case 'customer':
        return (
          <CustomerLink
            customerId={transaction.customer_id}
            customerName={getCustomerName(transaction)}
            className="btn btn-link btn-sm p-0"
          />
        );
      case 'type': return transaction.type.toUpperCase();
      case 'vehicle':
        return transaction.vehicle_id ? (
          <VehicleLink
            vehicleId={transaction.vehicle_id}
            vehicleInfo={getVehicleInfo(transaction)}
            className="btn btn-link btn-sm p-0"
          />
        ) : '-';
      case 'start_date': return transaction.start_date?.split('T')[0] || '-';
      case 'end_date': return transaction.end_date?.split('T')[0] || '-';
      case 'amount': return `$${(parseFloat(transaction.amount) + parseFloat(transaction.tax_amount || 0)).toFixed(2)}`;
      case 'actions':
        return (
          <>
            <Button variant="link" size="sm" onClick={() => navigate(`./${transaction.id}`)}>
              View
            </Button>
            {transaction.vehicle_id && (
              <Button
                variant="link"
                size="sm"
                onClick={() => handleGenerateContract(transaction.id, transaction.vehicle_id)}
                title="Generate Contract PDF"
                className="ms-3"
              >
                <img src={documentIcon} alt="Generate Contract" style={{ width: 16, height: 16 }} />
              </Button>
            )}
          </>
        );
      default: return null;
    }
  };

  return (
    <Container fluid className="py-4 domain-transactions">
      <Row className="mb-4">
        <Col>
          <h2>Transactions</h2>
        </Col>
      </Row>

      <Row className="mb-3 gap-2">
        {user?.role !== 'auditor' && (
          <Col xs="auto">
            <Button
              variant={showNewTransactionForm ? "secondary" : "primary"}
              onClick={() => setShowNewTransactionForm(!showNewTransactionForm)}
            >
              {showNewTransactionForm ? <><img src={closeIcon} alt="" className="icon-btn" />Cancel</> : <><img src={addIcon} alt="" className="icon-btn" />New Transaction</>}
            </Button>
          </Col>
        )}
        <Col xs="auto">
          <Button
            variant={showFilters ? "secondary" : "outline-primary"}
            onClick={() => {
              if (!showFilters) setLocalFilters(filters);
              setShowFilters(!showFilters);
            }}
          >
            {showFilters ? <><img src={closeIcon} alt="" className="icon-btn" />Close Filters</> : <><img src={searchIcon} alt="" className="icon-btn" />Filters</>}
          </Button>
        </Col>
        <Col xs="auto" className="ms-auto">
          <ColumnConfigDropdown allColumns={allColumns} isVisible={isVisible} toggleColumn={toggleColumn} />
        </Col>
      </Row>

      {showFilters && (
        <Card className="mb-3">
          <Card.Header className="text-white" style={{ backgroundColor: '#3b82f6' }}>
            <h3>Transaction Filters</h3>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <Form.Select name="type" value={localFilters.type} onChange={handleFilterChange}>
                    <option value="">All Types</option>
                    <option value="rental">Rental</option>
                    <option value="fee">Fee</option>
                    <option value="late_fee">Late Fee</option>
                    <option value="discount">Discount</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="start_date"
                    value={localFilters.start_date}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="end_date"
                    value={localFilters.end_date}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Store</Form.Label>
                  <Form.Select name="store_id" value={localFilters.store_id} onChange={handleFilterChange}>
                    <option value="">All Stores</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle ID</Form.Label>
                  <Form.Control
                    type="number"
                    name="vehicle_id"
                    value={localFilters.vehicle_id}
                    onChange={handleFilterChange}
                    placeholder="Filter by vehicle ID..."
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={applyFilters}>
                Apply Filters
              </Button>
              <Button variant="secondary" onClick={resetFilters}>
                Reset Filters
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {showNewTransactionForm && (
        <Card className="mb-3">
          <Card.Header className="text-white" style={{ backgroundColor: '#3b82f6' }}>
            <h3>New Transaction</h3>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleAddTransaction}>
              {formError && <Alert variant="danger">{formError}</Alert>}

              <Row>
                <Col md={6}>
                  <SearchableCustomerSelect
                    apiCall={apiCall}
                    selectedCustomerId={formData.customer_id}
                    onSelectCustomer={(customerId) => setFormData({ ...formData, customer_id: customerId })}
                    required={true}
                  />
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      name="type"
                      value={formData.type}
                      onChange={(e) => {
                        handleInputChange(e);
                        if (e.target.value === 'fee' || e.target.value === 'late_fee') {
                          setFormData(prev => ({
                            ...prev,
                            type: e.target.value,
                            store_id: '',
                            vehicle_id: '',
                            unit: 'day',
                            quantity: '',
                          }));
                        }
                      }}
                    >
                      <option value="rental">Rental Income</option>
                      <option value="fee">Other Charges</option>
                      <option value="late_fee">Late Fee</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={formData.type === 'rental' ? 4 : 12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{formData.type === 'late_fee' ? 'Fee Date' : 'Start Date'}</Form.Label>
                    <Form.Control
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
                {formData.type === 'rental' && (
                  <>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Unit</Form.Label>
                        <Form.Select
                          name="unit"
                          value={formData.unit}
                          onChange={handleInputChange}
                        >
                          <option value="day">Day</option>
                          <option value="week">Week</option>
                          <option value="month">Month</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Quantity</Form.Label>
                        <Form.Control
                          type="number"
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleInputChange}
                          min="1"
                        />
                        {formData.start_date && formData.quantity && (
                          <Form.Text className="text-muted">
                            {(() => {
                              const startDate = new Date(formData.start_date);
                              const endDate = new Date(startDate);
                              const quantity = parseInt(formData.quantity);

                              if (formData.unit === 'day') {
                                endDate.setDate(endDate.getDate() + quantity);
                              } else if (formData.unit === 'week') {
                                endDate.setDate(endDate.getDate() + quantity * 7);
                              } else if (formData.unit === 'month') {
                                endDate.setMonth(endDate.getMonth() + quantity);
                              }

                              return `End Date: ${endDate.toISOString().split('T')[0]}`;
                            })()}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>

              <Row>
                <Col md={12}>
                  <SearchableVehicleSelect
                    apiCall={apiCall}
                    selectedVehicleId={formData.vehicle_id}
                    onSelectVehicle={async (vehicleId) => {
                      setFormData(prev => ({ ...prev, vehicle_id: vehicleId }));
                      if (vehicleId) {
                        try {
                          const res = await apiCall(`${API_BASE_URL}/vehicles/${vehicleId}`);
                          setSelectedVehicle(res?.data || null);
                        } catch { setSelectedVehicle(null); }
                      } else {
                        setSelectedVehicle(null);
                      }
                    }}
                    storeId={formData.store_id || null}
                    required={formData.type === 'rental'}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Amount</Form.Label>
                    {(() => {
                      if (formData.type === 'rental' && selectedVehicle && formData.quantity) {
                        const vehicle = selectedVehicle;
                        if (vehicle) {
                          const pricing = pricingTiers[vehicle.class];
                          if (pricing) {
                            const quantity = parseInt(formData.quantity);
                            let calculatedAmount = 0;

                            if (formData.unit === 'day') {
                              calculatedAmount = pricing.daily * quantity;
                            } else if (formData.unit === 'week') {
                              calculatedAmount = pricing.weekly * quantity;
                            } else if (formData.unit === 'month') {
                              calculatedAmount = pricing.monthly * quantity;
                            }

                            return (
                              <>
                                <Form.Control
                                  type="number"
                                  name="amount"
                                  placeholder={calculatedAmount.toFixed(2)}
                                  value={formData.amount || ''}
                                  onChange={handleInputChange}
                                  step="0.01"
                                />
                                {!formData.amount && (
                                  <Form.Text className="text-muted">
                                    Auto-calculated: ${calculatedAmount.toFixed(2)} (edit to adjust)
                                  </Form.Text>
                                )}
                              </>
                            );
                          }
                        }
                      }
                      return (
                        <Form.Control
                          type="number"
                          name="amount"
                          value={formData.amount}
                          onChange={handleInputChange}
                          step="0.01"
                        />
                      );
                    })()}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Paid</Form.Label>
                    <Form.Select
                      name="isPaid"
                      value={formData.isPaid}
                      onChange={(e) => {
                        const syntheticEvent = {
                          target: { name: 'isPaid', value: e.target.value === 'true' }
                        };
                        handleInputChange(syntheticEvent);
                      }}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Tax Preview for Rentals */}
              {formData.type === 'rental' && selectedVehicle && formData.quantity && (() => {
                // Use manually entered amount, or fall back to auto-calculated amount from pricing tiers
                let effectiveAmount = parseFloat(formData.amount);
                if (!effectiveAmount && selectedVehicle) {
                  const pricing = pricingTiers[selectedVehicle.class];
                  if (pricing) {
                    const qty = parseInt(formData.quantity);
                    if (formData.unit === 'day') effectiveAmount = pricing.daily * qty;
                    else if (formData.unit === 'week') effectiveAmount = pricing.weekly * qty;
                    else if (formData.unit === 'month') effectiveAmount = pricing.monthly * qty;
                  }
                }
                if (!effectiveAmount || effectiveAmount <= 0) return null;
                const quantity = parseInt(formData.quantity);
                let rentalDays = quantity;
                if (formData.unit === 'week') rentalDays = quantity * 7;
                else if (formData.unit === 'month') rentalDays = quantity * 30;
                const taxResult = calculateTaxFrontend(selectedVehicle.store_id, effectiveAmount, rentalDays);
                if (taxResult.totalTax > 0) {
                  const grandTotal = effectiveAmount + taxResult.totalTax;
                  return (
                    <Row>
                      <Col md={12}>
                        <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#fff8f0', borderColor: '#e67e22' }}>
                          <strong style={{ color: '#e67e22' }}>Tax Estimate</strong>
                          <table className="mt-2" style={{ width: '100%' }}>
                            <tbody>
                              <tr>
                                <td>Subtotal (Rental)</td>
                                <td className="text-end">${effectiveAmount.toFixed(2)}</td>
                              </tr>
                              {taxResult.items.map((item, i) => (
                                <tr key={i} style={{ color: '#e67e22' }}>
                                  <td>{item.name} ({item.type === 'percentage' ? `${(item.rate * 100).toFixed(2)}%` : `$${item.rate.toFixed(2)}/day`})</td>
                                  <td className="text-end">${item.amount.toFixed(2)}</td>
                                </tr>
                              ))}
                              <tr style={{ borderTop: '1px solid #e67e22', fontWeight: 'bold' }}>
                                <td>Tax Total</td>
                                <td className="text-end">${taxResult.totalTax.toFixed(2)}</td>
                              </tr>
                              <tr style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                                <td>Grand Total (incl. tax)</td>
                                <td className="text-end">${grandTotal.toFixed(2)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </Col>
                    </Row>
                  );
                }
                return null;
              })()}

              {formData.isPaid && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Method</Form.Label>
                      <Form.Select
                        name="payment_method"
                        value={formData.payment_method}
                        onChange={handleInputChange}
                      >
                        {renderPaymentMethodOptions()}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button type="submit" variant="primary">
                Save Transaction
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body className="p-0">
          {filteredTransactions.length > 0 && (
            <div className="px-3 py-2 bg-light">
              <small className="text-muted">
                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} transactions
              </small>
            </div>
          )}

          <Table striped hover responsive>
          <thead>
            <tr>
              {visibleColumns.map(col => (
                <th key={col.key} className={col.className}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.length > 0 ? (
              filteredTransactions.map(transaction => (
                <tr key={transaction.id}>
                  {visibleColumns.map(col => (
                    <td key={col.key} className={col.className}>{renderCell(col.key, transaction)}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={visibleColumns.length} className="text-center py-4">
                  {filteredTransactions.length === 0 && filters.type ? 'No transactions match your filters' : 'No transactions found'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>

      {filteredTransactions.length > 0 && (
        <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
          <Button
            variant="secondary"
            onClick={goToPreviousPage}
            disabled={pagination.offset === 0}
          >
            &larr; Previous
          </Button>
          <span className="text-muted">
            Page {Math.floor(pagination.offset / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
          </span>
          <Button
            variant="secondary"
            onClick={goToNextPage}
            disabled={pagination.offset + pagination.limit >= pagination.total}
          >
            Next &rarr;
          </Button>
        </div>
      )}
    </Container>
  );
}

export default TransactionList;
